import 'swiper/css';
import 'swiper/css/pagination';

import { Swiper, SwiperSlide } from 'swiper/react';

import Image from 'next/image';
import { Pagination } from 'swiper/modules';
import React from 'react';
import { motion } from 'framer-motion';

type TestimonialProps = {
  testimonials: {
    rating: number;
    text: string;
    reviewerName: string;
    reviewerImage: string;
    reviewerDesignation: string;
    company: string;
    companyLogo: string;
    background: string;
  }[];
};

const Testimonial: React.FC<TestimonialProps> = ({ testimonials }) => {
  return (
    <section
      className="relative w-full py-16 bg-cover bg-center mt-16"
      aria-labelledby="testimonial-section"
    >
      <div
        className="absolute inset-0 bg-base-content rounded-br-4xl rounded-tl-4xl"
        role="presentation"
      ></div>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.2, duration: 0.6 }}
        className="relative z-10"
      >
        <h2 id="testimonial-section" className="sr-only">
          Customer Testimonials
        </h2>
        <Swiper
          slidesPerView={1}
          centeredSlides={true}
          spaceBetween={30}
          pagination={{
            clickable: true,
          }}
          modules={[Pagination]}
          className="mySwiper w-full mx-auto"
          breakpoints={{
            320: {
              slidesPerView: 1,
              spaceBetween: 20,
            },
            1024: {
              slidesPerView: 2,
              spaceBetween: 30,
            },
          }}
        >
          <div role="list">
            {testimonials.map((testimonial, index) => (
              <SwiperSlide key={index} aria-labelledby={`testimonial-${index}`}>
                <li
                  className="bg-white flex flex-row items-center relative overflow-hidden transition-all transform rounded-br-4xl rounded-tl-4xl mx-4"
                  role="listitem"
                  aria-labelledby={`testimonial-heading-${index}`}
                >
                  <div className="relative hidden sm:block w-90">
                    <Image
                      src={testimonial.background}
                      alt={`Background image for testimonial by ${testimonial.reviewerName}`}
                      width={90}
                      height={0}
                      className="w-90 object-cover h-[45vh] rounded-br-4xl"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-black/20 rounded-br-4xl pointer-events-none"></div>
                    {testimonial.companyLogo && (
                      <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2">
                        <Image
                          src={testimonial.companyLogo}
                          alt={`${testimonial.company} logo`}
                          width={60}
                          height={60}
                          className="w-18 h-18 object-contain filter brightness-0 invert"
                          loading="lazy"
                        />
                      </div>
                    )}
                  </div>
                  <div className="p-8">
                    <div
                      className="rating flex"
                      role="img"
                      aria-label={`${testimonial.rating} out of 5 stars rating for ${testimonial.reviewerName}`}
                    >
                      {[...Array(5)].map((_, i) => (
                        <div
                          key={i}
                          className={`mask mask-star-2 ${
                            i < testimonial.rating
                              ? 'bg-warning'
                              : 'bg-gray-300'
                          }`}
                          aria-hidden="true"
                          aria-label={`${i + 1} star`}
                          aria-current={
                            i + 1 === testimonial.rating ? 'true' : undefined
                          }
                        />
                      ))}
                    </div>
                    <p className="text-sm sm:text-lg md:text-lg lg:text-lg xl:text-lg font-normal text-base-content mt-8 text-left">
                      <q>{testimonial.text}</q>
                    </p>
                    <div className="flex flex-row items-center mt-8">
                      <Image
                        src={testimonial.reviewerImage}
                        alt={`Image of ${testimonial.reviewerName}`}
                        width={70}
                        height={70}
                        className="rounded-full border-4 border-white"
                        priority
                      />
                      <div className="text-left ml-4">
                        <h3
                          id={`testimonial-heading-${index}`}
                          className="text-sm sm:text-lg md:text-lg lg:text-lg xl:text-lg font-semibold text-base-content"
                        >
                          {testimonial.reviewerName}
                        </h3>
                        <p className="text-sm sm:text-lg md:text-lg lg:text-lg xl:text-lg font-normal text-base-content">
                          <span className="font-medium text-gray-800">
                            {testimonial.company}
                          </span>{' '}
                          <span className="text-primary">/</span>{' '}
                          {testimonial.reviewerDesignation}
                        </p>
                      </div>
                    </div>
                  </div>
                </li>
              </SwiperSlide>
            ))}
          </div>
        </Swiper>
      </motion.div>
    </section>
  );
};

export default Testimonial;
